import { useEffect } from "react";

import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useLazyApi } from "@bokio/mobile-web-shared/hooks/useApi/useApi";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import {
	SupportMessageCompletedType,
	SupportMessageModal,
} from "@bokio/settings/src/scenes/SupportMessages/SupportMessageModal/SupportMessageModal";
import { useRouter } from "@bokio/shared/containers/router/useRouter";
import { getRoute } from "@bokio/shared/route";

import type { ReactNode } from "react";

import SupportMessageType = m.Entities.Support.SupportMessageType;

export const useNextSupportMessageModal = (): ReactNode => {
	const { companyInfo } = useCompanyInfo();
	const { redirect } = useRouter();

	const checkIfPromotionModal = (
		supportMessageToShow: m.Bokio.Common.Contract.ViewData.SupportMessageActiveDto | undefined,
	): boolean => {
		if (supportMessageToShow === undefined) {
			return false;
		}

		const promotionModalTypes = [
			SupportMessageType.TrialToPaidPlan,
			SupportMessageType.FreeToPaidPlan,
			SupportMessageType.PremiumToBba,
			SupportMessageType.BbaToPremiumPlus,
		];

		return promotionModalTypes.includes(supportMessageToShow?.MessageType);
	};

	const [refreshBlockingSupportMessages, blockingSupportMessagesRequest] = useLazyApi(
		proxy.Settings.SupportMessagesController.Index.Get,
	);

	useEffect(() => {
		//ME: With this approach the support messages doesn't reload when you navigate between different company routes.
		if (companyInfo !== undefined) {
			refreshBlockingSupportMessages(companyInfo.Id, true);
		}
	}, [companyInfo, refreshBlockingSupportMessages]);

	const supportMessageToShow = !blockingSupportMessagesRequest.isLoading
		? blockingSupportMessagesRequest.data?.Data?.[0]
		: undefined;

	const isPromotionModal = checkIfPromotionModal(supportMessageToShow);

	const getPromotionModalRedirect = (SupportMessageType: m.Entities.Support.SupportMessageType): string => {
		switch (SupportMessageType) {
			case m.Entities.Support.SupportMessageType.PremiumToBba:
				return getRoute("bankList", {
					company: companyInfo.Id,
				});

			default:
				return getRoute("billing", {
					company: companyInfo.Id,
				});
		}
	};

	const onComplete = async (type?: SupportMessageCompletedType) => {
		await refreshBlockingSupportMessages(companyInfo.Id, true);

		if (!!supportMessageToShow && isPromotionModal && type === SupportMessageCompletedType.Resolved) {
			const redirectTo = getPromotionModalRedirect(supportMessageToShow.MessageType);

			redirect(redirectTo);
		}
	};

	if (supportMessageToShow) {
		return (
			<SupportMessageModal supportMessage={supportMessageToShow} onComplete={onComplete} companyId={companyInfo.Id} />
		);
	}

	return null;
};
